import { Layers, LayoutGrid, List as ListIcon, Table as TableIcon } from 'lucide-react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ViewModeOption } from '@/components/ui/view-mode-toggle';

/** Shared view-mode model for the CRM-style cabinet lists. */
export type CabinetViewMode = 'table' | 'list' | 'cards' | 'grouped';

export interface CabinetViewState {
  view: CabinetViewMode;
  setView: (view: CabinetViewMode) => void;
  /** DRF OrderingFilter value (e.g. `-created`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
}

/** One persisted (view-mode only) store per list. Call at module scope.
 * Bump `version` when changing `defaultView` so a stale persisted view is
 * dropped (migrate clears it → the new default applies on next load). */
export function createCabinetViewStore(
  name: string,
  defaultView: CabinetViewMode = 'table',
  version = 0,
) {
  return create<CabinetViewState>()(
    persist(
      (set) => ({
        view: defaultView,
        setView: (view) => set({ view }),
        ordering: undefined,
        setOrdering: (ordering) => set({ ordering }),
      }),
      {
        name,
        version,
        // On a version bump, discard the persisted view so the new default wins.
        migrate: () => ({}) as Partial<CabinetViewState>,
        partialize: (state) => ({ view: state.view }),
      },
    ),
  );
}

const MODE_META: Record<CabinetViewMode, ViewModeOption<CabinetViewMode>> = {
  table: { value: 'table', label: 'Table', icon: TableIcon },
  list: { value: 'list', label: 'List', icon: ListIcon },
  cards: { value: 'cards', label: 'Cards', icon: LayoutGrid },
  grouped: { value: 'grouped', label: 'Grouped', icon: Layers },
};

export function viewModes(modes: readonly CabinetViewMode[]): ViewModeOption<CabinetViewMode>[] {
  return modes.map((m) => MODE_META[m]);
}
